import { useCallback, useDeferredValue, useMemo } from "react";
import { useModel } from "@/contexts/ModelContext";
import { useFrame } from "@/contexts/FrameContext";
import { useMedia } from "@/contexts/MediaContext";
import { useAsyncBoundOperation } from "@/hooks/useApi";
import { Chip } from "@scripts/react/components/Chip";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent, AppSource } from "@/analytics/types";
import { SHOW_TEXT_PROMPT_SRC } from "@/constants/media";
import { useCurrentMedia, usePromptLabels } from "@/store";
import { useMediaUtils } from "@/hooks/useMediaUtils";

interface TextPromptProposal {
    prompt: string;
    active: boolean;
}

function useTextPromptProposalClick() {
    const { textPromptStr, setTextPromptStr } = useModel();
    const { track } = useAnalytics();

    return useCallback(
        (textPromptProposal: string) => {
            track({
                event: AnalyticsFlowEvent.SMART_CHIP_CLICKED,
                data: {
                    appSource: AppSource.RAPID,
                    chipText: textPromptProposal,
                    currentTextPrompts: textPromptStr
                        .split(",")
                        .map((s) => s.trim())
                        .filter((s) => s),
                    isAdding: !textPromptStr
                        .split(",")
                        .map((s) => s.trim())
                        .filter((s) => s)
                        .includes(textPromptProposal)
                }
            });

            setTextPromptStr((prevPromptStr: string) => {
                const parts = prevPromptStr
                    .split(",")
                    .map((s) => s.trim())
                    .filter((s) => s);
                const existsAtIndex = parts.indexOf(textPromptProposal);
                if (existsAtIndex > -1) {
                    parts.splice(existsAtIndex, 1);
                } else {
                    parts.push(textPromptProposal);
                }
                return parts.join(", ");
            });
        },
        [setTextPromptStr, track, textPromptStr]
    );
}

function ProposalChips({
    proposals,
    isDisabled,
    onProposalClick,
    showPoweredBy,
    isLoading,
    error,
    onRetryClick
}: {
    proposals: TextPromptProposal[];
    isDisabled: boolean;
    onProposalClick: (prompt: string) => void;
    showPoweredBy: boolean;
    isLoading?: boolean;
    error?: Error | null;
    onRetryClick?: () => void;
}) {
    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex justify-between text-xs font-medium text-tertiary">
                <p>Try these:</p>
                {showPoweredBy && SHOW_TEXT_PROMPT_SRC && (
                    <p>
                        Powered by{" "}
                        <a href="https://ai.meta.com/sam3/" target="_blank" rel="noreferrer">
                            SAM3
                        </a>
                    </p>
                )}
            </div>
            <div className="flex gap-1 overflow-x-auto">
                {error ? (
                    <Chip
                        key={"error"}
                        color="red"
                        className="interactive cursor-pointer select-none border border-destructive !bg-white hover:!border-destructive-hover"
                        onClick={onRetryClick}
                    >
                        Tap to retry
                        <i className="fa-solid fa-rotate-right" />
                    </Chip>
                ) : isLoading || !proposals.length ? (
                    <Chip
                        key={"isLoading"}
                        color="white"
                        className="animate-pulse cursor-not-allowed select-none"
                    >
                        Thinking...
                    </Chip>
                ) : (
                    <>
                        {proposals.slice(0, 4).map((proposal) => (
                            <Chip
                                key={proposal.prompt}
                                color={proposal.active ? "purple" : "white"}
                                className={
                                    !isDisabled
                                        ? proposal.active
                                            ? "cursor-pointer select-none border-purple-300 bg-tint hover:bg-tint-hover"
                                            : "cursor-pointer select-none hover:bg-gray-100"
                                        : `preset-filled disabled cursor-not-allowed`
                                }
                                onClick={() => {
                                    if (!isDisabled) {
                                        onProposalClick(proposal.prompt);
                                    }
                                }}
                            >
                                {proposal.prompt}
                            </Chip>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}

type TextPromptProposalChipsProps = { isDisabled: boolean; showPoweredBy?: boolean };

export const TextPromptProposalChips = ({
    isDisabled,
    showPoweredBy = false
}: TextPromptProposalChipsProps) => {
    const { textPromptStr } = useModel();
    const currentMedia = useCurrentMedia();
    const { getMediaItemCaptionProposals } = useMediaUtils();
    const { currentFrameUploadTextPromptProposals, setCurrentFrameUploadTextPromptProposals } =
        useFrame();
    const promptLabels = usePromptLabels();
    const prompts = useMemo(() => {
        return Array.from(promptLabels.values()).map((label) => label.name);
    }, [promptLabels]);

    const doCaptionInfer = useCallback(
        async (abortController: AbortController) => {
            if (!currentMedia?.frame) return;

            setCurrentFrameUploadTextPromptProposals(undefined);

            const textPromptProposals = await getMediaItemCaptionProposals(currentMedia.frame, {
                abortController
            });

            if (textPromptProposals && !abortController?.signal.aborted) {
                setCurrentFrameUploadTextPromptProposals(textPromptProposals);
                return textPromptProposals;
            }
        },
        [
            currentMedia?.frame,
            getMediaItemCaptionProposals,
            setCurrentFrameUploadTextPromptProposals
        ]
    );

    const {
        isLoading: isLoadingTextPromptCaption,
        error: errorTextPromptCaption,
        reset: resetTextPromptCaption,
        invoke: invokeTextPromptCaption
    } = useAsyncBoundOperation(doCaptionInfer, { autoInvoke: true });

    const onTextPromptProposalClick = useTextPromptProposalClick();

    const textPromptStrDeferred = useDeferredValue(textPromptStr);
    const currentFrameUploadTextPromptProposalsFiltered = useMemo<TextPromptProposal[]>(() => {
        if (!currentFrameUploadTextPromptProposals) {
            return [];
        }
        const textPrompts = textPromptStrDeferred.split(",").map((s) => s.trim());
        return currentFrameUploadTextPromptProposals
            .filter((proposal) => !prompts.includes(proposal))
            .map((proposal) => ({ prompt: proposal, active: textPrompts.includes(proposal) }));
    }, [currentFrameUploadTextPromptProposals, textPromptStrDeferred, prompts]);

    const onRetryClick = useCallback(() => {
        resetTextPromptCaption();
        invokeTextPromptCaption();
    }, [invokeTextPromptCaption, resetTextPromptCaption]);

    return (
        <ProposalChips
            proposals={currentFrameUploadTextPromptProposalsFiltered}
            isDisabled={isDisabled}
            onProposalClick={onTextPromptProposalClick}
            showPoweredBy={showPoweredBy}
            isLoading={isLoadingTextPromptCaption}
            error={errorTextPromptCaption}
            onRetryClick={onRetryClick}
        />
    );
};

export const StableTextPromptProposalChips = ({
    isDisabled,
    showPoweredBy = false
}: TextPromptProposalChipsProps) => {
    const { textPromptStr } = useModel();
    const { stableTextPromptProposals } = useMedia();

    const onTextPromptProposalClick = useTextPromptProposalClick();

    const textPromptStrDeferred = useDeferredValue(textPromptStr);
    const stableTextPromptProposalsFiltered = useMemo<TextPromptProposal[]>(() => {
        if (!stableTextPromptProposals) {
            return [];
        }
        const textPrompts = textPromptStrDeferred.split(",").map((s) => s.trim());
        return stableTextPromptProposals.map((proposal) => ({
            prompt: proposal,
            active: textPrompts.includes(proposal)
        }));
    }, [stableTextPromptProposals, textPromptStrDeferred]);

    const isLoading = stableTextPromptProposals === undefined;

    // Don't render anything if we have no proposals (but finished loading)
    if (!isLoading && stableTextPromptProposalsFiltered.length === 0) {
        return null;
    }

    return (
        <ProposalChips
            proposals={stableTextPromptProposalsFiltered}
            isDisabled={isDisabled}
            onProposalClick={onTextPromptProposalClick}
            showPoweredBy={showPoweredBy}
            isLoading={isLoading}
        />
    );
};
